<template>
	<div>
		<div v-show="Step1IsValid" class="bg-grey-100 p-4 ">
			<h2 class="leading-none font-700 text-lg mb-4">{{ $t("reservationSummary.title") }}</h2>
			<div class="mb-4">
				<div class="mb-2">
					<div class="leading-none font-700 uppercase">{{ $t("reservationSummary.pickUp") }}</div>
					<div>{{ PickupDateTimeFormatted }}</div>
					<div>{{ $t("reservationSummary.location") }}: {{ LocationName }}</div>
					<div v-show="LocationIsAirport">
						<div>{{ $t("reservationSummary.flightArrivalDateTime") }}: {{ ArrivalDateTimeFormatted }}</div>
						<div>{{ $t("reservationSummary.flightNumber") }}: {{ reservation.flight.flightNumber }}</div>
					</div>
					<div v-show="LocationIsCustom">{{ $t("reservationSummary.customAddress") }}:
						{{ reservation.customLocation }}</div>
				</div>
				<div class="mb-2">
					<div class="leading-none font-700 uppercase">{{ $t("reservationSummary.return") }}</div>
					<div>{{ ReturnDateTimeFormatted }}</div>
					<div>{{ $t("reservationSummary.location") }}: {{ ReturnLocationName }}</div>
				</div>
			</div>

			<div v-show="Step2IsValid && CarTotalRate && CarTotalRate > 0" class="mb-4">
				<div>
					<div class="leading-none font-700 uppercase">{{ CarName }}</div>

					<div>
						<div v-if="getPriceDetails.length > 1">
							<div v-for="(detail, index) in getPriceDetails" :key="'priceDetail' + index"
								class="flex mt-2">
								<div class="w-1/2">
									{{ detail.days }} {{ $t("reservationSummary.days") }} x {{ detail.price | toCurrency
									}}
									<span v-if="index === 0">{{ $t("reservationSummary.lowSeason") }}</span>
									<span v-else-if="index === 1">{{ $t("reservationSummary.highSeason") }}</span>
								</div>
								<div class="w-1/2 text-right italic">{{ detail.total | toCurrency }}</div>
							</div>
						</div>

						<div v-else class="flex">
							<div class="w-1/2">{{ NumberOfDays }}x {{ CarRate | toCurrency }}</div>
							<div class="w-1/2 text-right italic">{{ CarTotalRate | toCurrency }}</div>
						</div>
					</div>

					<div v-for="(discount, j) of ApplicableDiscounts" :key="'discount' + j" class="flex">
						<div class="w-1/2">{{ discount.name }}:</div>
						<div class="w-1/2 text-right italic">{{ getDiscountPriceByIndex(j) | toCurrency }}</div>
					</div>
					<div v-show="HasDiscounts" class="flex">
						<div class="w-1/2"></div>
						<div class="w-1/2 text-right italic">{{ CarPriceWithDiscount | toCurrency }}</div>
					</div>
				</div>
			</div>
			<div v-show="Step3IsValid" class="mb-4">
				<div v-if="SortedSelectedAdditionalChargesInsurancePrices && SortedSelectedAdditionalChargesInsurancePrices.length > 0"
					class="mb-4">
					<div class="leading-none font-700 uppercase">{{ $t("reservationSummary.additionalChargesInsurances")
						}}</div>
					<div v-for="(ssacip, i) of SortedSelectedAdditionalChargesInsurancePrices" :key="'ssacip' + i"
						class="flex">
						<div class="w-1/2">{{ getInsuranceQuantityByIndex(i) }}{{ getTranslatedLabelById(ssacip.id) }}:
						</div>
						<div class="w-1/2 text-right italic">{{ getAdditionalChargeInsuranceTotalRateByIndex(i) |
							toCurrency }}</div>
					</div>
				</div>

				<div v-if="SortedSelectedAdditionalChargesServicePrices && SortedSelectedAdditionalChargesServicePrices.length > 0"
					class="mb-4">
					<div class="leading-none font-700 uppercase">{{ $t("reservationSummary.additionalChargesServices")
						}}</div>

					<div v-if="SortedSelectedAdditionalChargesServicePrices.length == 1 && type == 'Dive'" class="flex">
						<div v-if="SortedSelectedAdditionalChargesServicePrices[0].selected_quantity > 1" class="w-1/2">
							{{
								$t("reservationSummary.daysOfUnlimitedAir",
									[SortedSelectedAdditionalChargesServicePrices[0].selected_quantity])
							}}</div>
						<div v-else class="w-1/2">{{
							$t("reservationSummary.dayOfUnlimitedAir",
								[SortedSelectedAdditionalChargesServicePrices[0].selected_quantity])
							}}</div>
						<div class="w-1/2 text-right italic">{{ getAdditionalChargeServiceTotalRateByIndex(0) |
							toCurrency }}</div>
					</div>

					<div v-else v-for="(ssacsp, i) of SortedSelectedAdditionalChargesServicePrices" :key="'ssacsp' + i"
						class="flex">
						<div class="w-1/2">{{ getServiceQuantityByIndex(i) }}{{ getTranslatedLabelById(ssacsp.id) }}
						</div>
						<div class="w-1/2 text-right italic">{{ getAdditionalChargeServiceTotalRateByIndex(i) |
							toCurrency }}</div>
					</div>

				</div>

				<div v-if="ApplicableTaxes && ApplicableTaxes.length > 0" class="mb-4">
					<div class="leading-none font-700 uppercase">{{ $t("reservationSummary.additionalTaxes") }}</div>
					<div v-for="(tax, j) of ApplicableTaxes" :key="'tax' + j" class="flex">
						<div class="w-1/2">{{ tax.name }}:</div>
						<div class="w-1/2 text-right italic">{{ getTaxPriceByIndex(j) | toCurrency }}</div>
					</div>
				</div>

				<div>
					<div class="flex items-center">
						<div class="leading-none font-700 uppercase">{{ $t("reservationSummary.total") }}</div>
						<div class="flex-grow text-right font-700 italic text-lg text-brand-1">
							<div class="hidden xl:inline-block button button--arrow button--brand-3 mr-8 font-300">
								<span class="font-700 mr-2">{{ $t("reservationSummary.noSurprises") }}</span>
								<span class="italic">{{ $t("reservationSummary.finalPrice") }}</span>
							</div>
							{{ TotalPrice | toCurrency }}
						</div>
					</div>
				</div>
			</div>
			<div v-show="Step4IsValid">
				<div class="leading-none font-700 uppercase">{{ $t("reservationSummary.contactInformation") }}</div>
				<div class="mb-4">
					<div>{{ ContactFullName }}</div>
					<div v-show="ContactAddress">{{ ContactAddress }}</div>
					<div>{{ ContactCountry }}</div>
				</div>
				<div class="mb-4">
					<div>{{ ContactEmail }}</div>
					<div v-show="ContactDateOfBirth">{{ $t("reservationSummary.contact.dateOfBirth") }}:
						{{ ContactDateOfBirth }}</div>
				</div>
			</div>
		</div>

		<div class="bg-grey-100 p-4 mb-4" v-if="false">
			<div class="text-right">
				<button @click.prevent="next()" v-show="showNextButton" class="button button--brand-1">
					{{ $t("reservation.next") }}
					<i class="icon msfas msfas-angle-right ml-1"></i>
				</button>
				<span v-show="!showNextButton" class="button button--disabled outline">
					{{ $t("reservation.next") }}
					<i class="icon msfas msfas-angle-right ml-1"></i>
				</span>
			</div>
		</div>
	</div>
</template>

<script>
  import ReservationHelper from '@/mixins/ReservationHelper';

  export default {
    name: 'ReservationSummary',
    mixins: [ReservationHelper],
    props: {
        reservation: Object,
        step: Number
    },
    computed: {
      showNextButton: function() {
        return (this.step === 1 && this.Step1IsValid) || (this.step === 2 && this.Step2IsValid) || (this.step === 3 && this.Step3IsValid) || (this.step === 4 && this.Step4IsValid) || (this.step === 5 && this.Step5IsValid)
      }
    },
    methods: {
      next() {
        this.$emit('next')
      }
    }
  }
</script>
